/**
 * LCFPILayout.js — Executive Six-Tab Platform Layout
 * IC Mode design language: dark, premium, DFI-ready.
 * Six top-level tabs replace the complex sidebar.
 * All existing functionality is preserved — reorganised, not deleted.
 */
import React, { useState } from "react";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useLCFPI } from "@/context/LCFPIContext";
import { useAuth } from "@/context/AuthContext";
import { LCFPIErrorBoundary } from "@/components/lcfpi/LCFPIPageShell";
import GlobalSearch from "@/components/lcfpi/GlobalSearch";
import {
  LayoutDashboard, GitBranch, FolderOpen, DollarSign,
  Leaf, Shield, Monitor, LogOut, Bell, ChevronRight, Search
} from "lucide-react";

// ── Six top-level tabs ────────────────────────────────────────────────────────
const TABS = [
  { id: "dashboard",  label: "Dashboard",  icon: LayoutDashboard, path: "/lcfpi/dashboard",   accent: "#3b82f6" },
  { id: "pipeline",   label: "Pipeline",   icon: GitBranch,        path: "/lcfpi/pipeline",    accent: "#0891b2" },
  { id: "projects",   label: "Projects",   icon: FolderOpen,       path: "/lcfpi/projects",    accent: "#7c3aed" },
  { id: "finance",    label: "Finance",    icon: DollarSign,       path: "/lcfpi/finance",     accent: "#059669" },
  { id: "impact",     label: "Impact",     icon: Leaf,             path: "/lcfpi/impact",      accent: "#16a34a" },
  { id: "governance", label: "Governance", icon: Shield,           path: "/lcfpi/governance",  accent: "#d97706" },
];

function getActiveTab(pathname) {
  const seg = pathname.split("/")[2] || "";
  const direct = TABS.find(t => t.id === seg);
  if (direct) return direct;
  // Legacy route mapping
  const legacy = {
    submit: "projects", workflow: "projects", assessment: "projects",
    readiness: "projects", "project-health": "projects",
    "eco-labels": "projects", "pdd-builder": "projects",
    "eoi-generator": "projects", "investment-profile": "readiness",
    "dfi-matching": "finance", "investor-workspace": "finance",
    "eoi-tracker": "finance", portfolio: "finance",
    monetisation: "impact", poa: "impact", "poa-deal-room": "impact",
    "data-room": "governance", "gov-workspace": "governance",
    "audit-trail": "governance", reports: "governance",
    settings: "governance", registry: "governance",
    digest: "pipeline",
  };
  const mapped = legacy[seg];
  return TABS.find(t => t.id === mapped) || TABS[0];
}

export default function LCFPILayout() {
  const { kpis } = useLCFPI();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); };
  const activeTab = getActiveTab(location.pathname);

  // Notification count from KPIs
  const pendingCount = kpis?.pending_review ?? 0;

  return (
    <LCFPIErrorBoundary>
      <div
        data-testid="lcfpi-executive-layout"
        style={{
          minHeight: "100vh",
          background: "#060d1a",
          color: "#e2e8f0",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'Inter', 'Georgia', sans-serif",
        }}
      >
        {/* ── Top Header ───────────────────────────────────────────────────── */}
        <header style={{
          background: "#070e1c",
          borderBottom: "1px solid #1e293b",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 52,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}>
          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: "linear-gradient(135deg,#1d4ed8,#059669)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Leaf size={14} color="#fff" />
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: ".02em", lineHeight: 1 }}>LCFPI</p>
              <p style={{ fontSize: 8, color: "#475569", letterSpacing: ".06em", textTransform: "uppercase" }}>Lagos Climate Finance Pipeline</p>
            </div>
          </div>

          {/* ── Six-tab navigation ────────────────────────────────────────── */}
          <nav style={{ display: "flex", gap: 2, alignItems: "center" }}>
            {TABS.map(tab => {
              const isActive = activeTab.id === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`lcfpi-tab-${tab.id}`}
                  onClick={() => navigate(tab.path)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "5px 12px", borderRadius: 8,
                    background: isActive ? `${tab.accent}18` : "transparent",
                    border: `1px solid ${isActive ? tab.accent + "50" : "transparent"}`,
                    color: isActive ? "#fff" : "#64748b",
                    fontSize: 12, fontWeight: isActive ? 700 : 500,
                    cursor: "pointer", transition: "all .15s",
                    borderBottom: isActive ? `2px solid ${tab.accent}` : "2px solid transparent",
                    borderRadius: "8px 8px 0 0",
                  }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = "#cbd5e1"; e.currentTarget.style.background = "#1e293b"; }}}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = "#64748b"; e.currentTarget.style.background = "transparent"; }}}
                >
                  <Icon size={12} color={isActive ? tab.accent : "currentColor"} />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Right actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Search */}
            <button onClick={() => setSearchOpen(v => !v)}
              data-testid="lcfpi-search-btn"
              style={{ background: "#1e293b", border: "none", borderRadius: 8, padding: "5px 10px", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
              <Search size={12} />
              <span style={{ fontSize: 11 }}>Search</span>
            </button>

            {/* Notifications */}
            {pendingCount > 0 && (
              <button onClick={() => navigate("/lcfpi/governance")}
                data-testid="lcfpi-notif-btn"
                style={{ position: "relative", background: "#1e293b", border: "none", borderRadius: 8, padding: "5px 8px", cursor: "pointer" }}>
                <Bell size={12} color="#f59e0b" />
                <span style={{
                  position: "absolute", top: -3, right: -3,
                  background: "#ef4444", borderRadius: "50%",
                  width: 14, height: 14, fontSize: 8, fontWeight: 800,
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                }}>{pendingCount}</span>
              </button>
            )}

            {/* Presentation Mode */}
            <button onClick={() => navigate("/lcfpi/ic-mode")}
              data-testid="lcfpi-presentation-btn"
              style={{
                display: "flex", alignItems: "center", gap: 5,
                background: "linear-gradient(135deg, #1d4ed8, #1e40af)",
                border: "1px solid #2563eb50",
                borderRadius: 8, padding: "5px 10px",
                color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>
              <Monitor size={11} />
              Presentation Mode
            </button>

            {/* User + Logout */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, borderLeft: "1px solid #1e293b", paddingLeft: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1e40af,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff" }}>
                {(user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase()}
              </div>
              <div>
                <p style={{ fontSize: 10, fontWeight: 600, color: "#cbd5e1", lineHeight: 1 }}>{user?.first_name || user?.email?.split("@")[0] || "User"}</p>
                <p style={{ fontSize: 8, color: "#475569", textTransform: "capitalize" }}>{user?.role || "user"}</p>
              </div>
              <button onClick={handleLogout} data-testid="lcfpi-logout-btn"
                style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", padding: 4 }}
                title="Logout">
                <LogOut size={12} />
              </button>
            </div>
          </div>
        </header>

        {/* ── Active tab label strip ─────────────────────────────────────── */}
        <div style={{
          background: "#070e1c",
          borderBottom: `1px solid ${activeTab.accent}22`,
          padding: "6px 24px",
          display: "flex", alignItems: "center", gap: 8,
          flexShrink: 0,
        }}>
          <div style={{ width: 4, height: 16, borderRadius: 2, background: activeTab.accent }} />
          <p style={{ fontSize: 11, fontWeight: 700, color: activeTab.accent, textTransform: "uppercase", letterSpacing: ".1em" }}>
            {activeTab.label}
          </p>
          <ChevronRight size={10} color="#334155" />
          <p style={{ fontSize: 11, color: "#475569" }}>
            Lagos Climate Finance Pipeline Initiative
          </p>

          {/* Tab-specific KPI pills */}
          {activeTab.id === "pipeline" && kpis?.finance_ready && (
            <span style={{ marginLeft: "auto", background: "#059669", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, color: "#fff" }}>
              {kpis.finance_ready} Finance Ready
            </span>
          )}
          {activeTab.id === "dashboard" && kpis?.total_projects > 0 && (
            <span style={{ marginLeft: "auto", background: "#1e40af", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, color: "#fff" }}>
              {kpis.total_projects} Active Projects
            </span>
          )}
        </div>

        {/* ── Global Search Overlay ─────────────────────────────────────── */}
        {searchOpen && (
          <div style={{ position: "fixed", inset: 0, background: "#000000aa", zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 80 }}
            onClick={() => setSearchOpen(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: 600, maxWidth: "90vw" }}>
              <GlobalSearch onClose={() => setSearchOpen(false)} />
            </div>
          </div>
        )}

        {/* ── Main content area ─────────────────────────────────────────── */}
        <main style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          <Outlet />
        </main>
      </div>
    </LCFPIErrorBoundary>
  );
}
